import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useTheme } from '../contexts/ThemeContext';
import { useDesign } from '../contexts/DesignContext';

// Define data generically to handle different chart types
interface ChartData {
  day: number;
  [key: string]: number;
}

// Define props for the component
interface ChartProps {
  title: string;
  subtitle?: string;
  type: 'bar' | 'line';
  data: ChartData[];
  series: SeriesConfig[];
  yAxisLabel?: string;
  yAxisUnit?: string;
  yAxisTicks?: number[];
  yAxisDomain?: [number, number];
  stacked?: boolean;
}

// Series configuration
interface SeriesConfig {
  dataKey: string;
  label: string;
}

// Define props for the custom tick component
interface CustomTickProps {
  x: number;
  y: number;
  payload: {
    value: number;
  };
}

// Custom tooltip that supports dark/light theme
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({ active, payload, label }) => {
  const { theme } = useTheme();
  const { design } = useDesign();
  const isDark = theme === 'dark';

  if (active && payload && payload.length) {
    return (
      <div className="p-2 border shadow-md rounded" style={{ 
        backgroundColor: isDark ? design.colors.darkBackgroundSecondary : 'white',
        borderColor: isDark ? '#374151' : '#E5E7EB',
      }}>
        <p className="font-normal" style={{ 
          color: isDark ? design.colors.textDark.secondary : design.colors.textLight.secondary,
          fontSize: `${design.typography.fontSizes.base}px`
        }}>{`Day ${label}`}</p>
        {payload.map((entry, index) => (
          <div key={`item-${index}`} className="flex items-center gap-2" style={{ fontSize: `${design.typography.fontSizes.sm}px`, fontWeight: 300 }}>
            <div style={{ 
              backgroundColor: entry.color, 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%' 
            }}></div>
            <p style={{ color: isDark ? design.colors.textDark.primary : design.colors.textLight.primary }}>
              {`${entry.name}: ${Math.round(Number(entry.value))}%`}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const TimeSpanningChart: React.FC<ChartProps> = ({ 
  title, 
  subtitle, 
  type,
  data, 
  series,
  yAxisLabel = '',
  yAxisUnit = '',
  yAxisTicks,
  yAxisDomain = [0, 100],
  stacked = false
}) => {
  const { theme } = useTheme();
  const { design } = useDesign();
  const isDark = theme === 'dark';
  
  // Background color based on theme
  const bgColor = isDark ? design.colors.darkBackground : design.colors.lightBackgroundSecondary;
  const textColor = isDark ? design.colors.textDark.secondary : design.colors.textLight.secondary;
  
  // Get colors from design config
  const chartColors = design.colors.chartColors;
  
  // Custom tick component extracted to handle the conditional rendering
  const CustomTick = (props: CustomTickProps) => {
    const { x, y, payload } = props;
    // Only show a subset of days like in the image
    if (payload.value % 4 !== 1) {
      // Return an empty group instead of null
      return <g></g>;
    }
    
    // Find the last day in the dataset to make sure we place the 'Day' label at the last visible tick
    const lastDay = data[data.length - 1]?.day ?? 29;
    const isLastValue = payload.value === (lastDay - ((lastDay - 1) % 4));
    
    return (
      <g transform={`translate(${x},${y + 20})`}>
        {isLastValue && (
          <text 
            x="30" 
            y="0" 
            dx="-20"
            dy="-50" 
            textAnchor="middle"
            transform="rotate(90)"
            fill={isDark ? "#A3A3A3" : "#666666"}
            fontSize={`${design.typography.fontSizes.sm}px`}
          >
            Day
          </text>
        )}
        <circle 
          cx="0" 
          cy="0" 
          r="15" 
          fill={isDark ? "#374151" : "#E5E7EB"} 
        />
        <text 
          x="0" 
          y="0" 
          dy="5" 
          textAnchor="middle" 
          fill={isDark ? "#E1E1E6" : "black"} 
          fontSize="12"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  const renderChart = () => {
    if (type === 'bar') {
      return (
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
          barSize={8}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} />
          <XAxis 
            dataKey="day" 
            axisLine={false}
            tickLine={false}
            tick={CustomTick}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            ticks={yAxisTicks}
            domain={yAxisDomain}
            tick={{ fill: isDark ? '#A3A3A3' : '#666666', fontSize: `${design.typography.fontSizes.sm}px` }}
            label={{ 
              value: yAxisLabel, 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: isDark ? '#A3A3A3' : '#666666', fontSize: `${design.typography.fontSizes.sm}px` }
            }}
            tickFormatter={value => (yAxisUnit ? `${Math.round(Number(value))}${yAxisUnit}` : `${value}`)}
          />
          <Tooltip content={<CustomTooltip />} />
          
          {series.map((item, index) => (
            <Bar 
              key={item.dataKey}
              dataKey={item.dataKey} 
              name={item.label}
              stackId={stacked ? "a" : undefined}
              fill={chartColors[index % chartColors.length]} 
            />
          ))}
        </BarChart>
      );
    } else {
      return (
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} />
          <XAxis 
            dataKey="day" 
            axisLine={false}
            tickLine={false}
            tick={CustomTick}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            domain={yAxisDomain}
            ticks={yAxisTicks}
            tick={{ fill: isDark ? '#A3A3A3' : '#666666', fontSize: `${design.typography.fontSizes.sm}px` }}
            label={{ 
              value: yAxisLabel, 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: isDark ? '#A3A3A3' : '#666666', fontSize: `${design.typography.fontSizes.sm}px` }
            }}
            tickFormatter={value => (yAxisUnit ? `${Math.round(Number(value))}${yAxisUnit}` : `${value}`)}
          />
          <Tooltip content={<CustomTooltip />} />
          
          {series.map((item, index) => (
            <Line
              key={item.dataKey}
              type="monotone"
              dataKey={item.dataKey}
              name={item.label}
              stroke={chartColors[index % chartColors.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 1 }}
            />
          ))}
        </LineChart>
      );
    }
  };

  // Generate legend items manually
  const renderLegendItems = () => {
    return (
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-1 px-6 pb-4">
        {series.map((item, index) => (
          <div key={item.dataKey} className="flex items-center">
            <div 
              className="w-5 h-5 mr-2" 
              style={{ 
                backgroundColor: chartColors[index % chartColors.length],
                borderRadius: type === 'line' ? '50%' : '3px'
              }}
            ></div>
            <span style={{ 
              fontSize: `${design.typography.fontSizes.base}px`, 
              fontWeight: 500,
              color: isDark ? design.colors.textDark.secondary : design.colors.textLight.secondary 
            }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="rounded-3xl shadow-sm h-full flex flex-col" style={{ backgroundColor: bgColor }}>
      <div className={`mb-${design.layout.margins.section} p-${design.layout.padding.card}`}>
        <div className="text-2xl font-normal" style={{ color: textColor, fontFamily: design.typography.fontFamily }}>{title}</div>
        {subtitle && (
          <div className="text-2xl font-normal" style={{ color: textColor, fontFamily: design.typography.fontFamily }}>{subtitle}</div>
        )}
      </div>
      
      <ResponsiveContainer width="100%" height={280} className="flex-grow">
        {renderChart()}
      </ResponsiveContainer>
      
      {renderLegendItems()}
    </div>
  );
};

export default TimeSpanningChart;